import { useEffect, useState } from 'react';
import { useSetRecoilState } from 'recoil';
import { authVersionState } from '../state.js';
import { setCurrentUsername } from '../api.js';
import { getAuthMode } from '../authApi.js';
import { AgioLogoAnimated } from '../components/AgioLogoAnimated.js';
import { LoginFlow } from '../components/login/LoginFlow.js';

/**
 * Login. Two modes, from /api/auth/mode:
 *  - password: our own sign-in — password, then the authenticator app, with a
 *    "choose a password" or "scan a QR" step whenever the server says one is
 *    owed. See LoginFlow.
 *  - stub (AGIO_AUTH_DISABLED=1): type a username. LOCAL DEV ONLY.
 * Brand-forward: animated letter-draw logo over a soft radial glow.
 */
export function LoginPage() {
  const [mode, setMode] = useState<'loading' | 'password' | 'stub'>('loading');
  const [username, setUsername] = useState('');
  const bumpAuth = useSetRecoilState(authVersionState);

  useEffect(() => {
    getAuthMode().then((m) => setMode(m.mode === 'stub' ? 'stub' : 'password'));
  }, []);

  if (mode === 'loading') return null;

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden">
      {/* ambient brand glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(60rem 32rem at 50% -8%, oklch(35% 0.08 255 / 0.5), transparent 65%),' +
            'radial-gradient(40rem 24rem at 85% 110%, oklch(30% 0.06 200 / 0.45), transparent 60%)',
        }}
      />

      <div className="relative flex w-full max-w-md flex-col items-center px-6">
        <AgioLogoAnimated className="mb-4 w-[min(300px,60vw)]" />
        <p className="mb-10 text-sm tracking-wide text-muted-foreground">פורטל לקוחות · ניהול השקעות</p>

        <div className="w-full">
          {mode === 'password' ? (
            <LoginFlow onDone={() => bumpAuth((v) => v + 1)} />
          ) : (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (!username.trim()) return;
                setCurrentUsername(username.trim());
                bumpAuth((v) => v + 1);
              }}
            >
              <input
                dir="ltr"
                className="mb-4 w-full rounded-md border border-transparent bg-white px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 outline-none focus:border-ring"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="admin@agio.local"
                autoFocus
              />
              <button
                type="submit"
                className="w-full rounded-md bg-primary px-4 py-2.5 font-semibold text-primary-foreground transition-opacity hover:opacity-90"
              >
                כניסה
              </button>
              <p className="mt-4 text-xs text-warning">⚠ AGIO_AUTH_DISABLED=1 — מצב פיתוח בלבד</p>
            </form>
          )}
        </div>

        <p className="mt-8 text-xs text-muted-foreground">agio · דוח ניהול השקעות</p>
      </div>
    </div>
  );
}
